import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import OfferForm from "@/components/OfferForm";
import { getWillById } from "../services/willService";
import { createOfferWithMedia } from "../services/offerService";
import { getAccessToken } from "../services/authService";
import { createOffer, getAiSuggestedOffer, regenerateAiSuggestion } from "@/services/offerService";
import MediaDisplay from "@/components/MediaDisplay";
import MediaViewer from "@/components/MediaViewer";
import NavBar from "@/components/NavBar";

interface ConflictError {
  existingOfferId: string;
  error: string;
  message: string;
  status: number;
}

const MakeOffer = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { will: any };
  const [contactInfo, setContactInfo] = useState("");
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [willData, setWillData] = useState(state?.will);
  const [isLoadingWill, setIsLoadingWill] = useState(!state?.will);
  const [isMediaViewerOpen, setIsMediaViewerOpen] = useState(false);
  const [initialMediaIndex, setInitialMediaIndex] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [aiSuggestion, setAiSuggestion] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Process AI suggestion data to ensure it's properly formatted
  const processAiSuggestion = (data: any) => {
    console.log('Processing AI suggestion data:', data);
    
    // If data is undefined or null, return empty object
    if (!data) {
      return { 
        description: '', 
        price: '', 
        currency: 'ILS', 
        estimatedCompletionDate: '', 
        providerContactInfo: contactInfo 
      };
    }
    
    // If data is a string (possibly JSON), try to parse it
    if (typeof data === 'string') {
      try {
        return processAiSuggestion(JSON.parse(data));
      } catch (e) {
        console.error('Failed to parse AI suggestion data as JSON:', e);
        // If it's just a string and not JSON, use it as the description
        return { 
          description: data, 
          price: '', 
          currency: 'ILS', 
          estimatedCompletionDate: '', 
          providerContactInfo: contactInfo 
        };
      }
    }
    
    // Handle if the real data is nested within a property like 'data', 'suggestion', 'content', etc.
    const possibleContainers = ['data', 'suggestion', 'content', 'result', 'offer', 'response'];
    for (const container of possibleContainers) {
      if (data[container] && typeof data[container] === 'object') {
        return processAiSuggestion(data[container]);
      }
    }
    
    // If data is already an object with the right structure, return it with proper defaults
    if (data && typeof data === 'object') {
      // Process price data - ensure it's in a usable format
      let processedPrice = '';
      
      if (data.price !== undefined && data.price !== null) {
        // Handle numeric prices
        if (typeof data.price === 'number') {
          processedPrice = data.price.toString();
        } 
        // Handle string prices with potential currency symbols
        else if (typeof data.price === 'string') {
          // Remove currency symbols and non-numeric characters except decimal point
          processedPrice = data.price.replace(/[^\d.]/g, '');
          
          // Check if it's a valid number after cleaning
          if (processedPrice && !isNaN(parseFloat(processedPrice))) {
            processedPrice = parseFloat(processedPrice).toString();
          }
        }
      }
      
      console.log('Processed price for AI suggestion:', processedPrice);
      
      return {
        description: data.description || '',
        price: processedPrice,
        currency: data.currency || 'ILS',
        estimatedCompletionDate: data.estimatedCompletionDate || data.fulfillmentDate || data.dueDate || '',
        providerContactInfo: data.providerContactInfo || data.contactInfo || contactInfo
      };
    }
    
    // Fallback to empty object if data is invalid
    return { 
      description: '', 
      price: '', 
      currency: 'ILS', 
      estimatedCompletionDate: '', 
      providerContactInfo: contactInfo 
    };
  };

  // Fetch AI suggestion for the offer
  const fetchAiSuggestion = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getAiSuggestedOffer(id || '');
      const formattedSuggestion = processAiSuggestion(response);
      setAiSuggestion(formattedSuggestion);
    } catch (err) {
      console.error('Error fetching AI suggestion:', err);
      setError('Failed to get AI suggestion. You can still create an offer manually.');
    } finally {
      setLoading(false);
    }
  };

  // Regenerate AI suggestion
  const handleRegenerateSuggestion = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await regenerateAiSuggestion(id || '');
      const formattedSuggestion = processAiSuggestion(response);
      setAiSuggestion(formattedSuggestion);
      toast.success('New AI suggestion generated');
    } catch (err) {
      console.error('Error regenerating AI suggestion:', err);
      setError('Failed to regenerate AI suggestion.');
      toast.error('Failed to regenerate suggestion');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!willData && id) {
      setIsLoadingWill(true);
      getWillById(id)
        .then(data => {
          setWillData({
            id: data.id,
            title: data.title,
            description: data.description,
            category: data.category,
            urgency: data.urgency || 'medium',
            reward: data.budget && data.currency ? 
              `${data.currency === 'ILS' ? '₪' : data.currency === 'USD' ? '$' : data.currency === 'EUR' ? '€' : '£'}${data.budget}` : null,
            fulfillmentTime: data.fulfillmentTime,
            wanterName: data.wanterName,
            media: data.media
          });
          setIsLoadingWill(false);
        })
        .catch(err => {
          console.error('Failed to load will:', err);
          toast.error("Failed to load will data");
          setIsLoadingWill(false);
          navigate('/');
        });
    }
  }, [id, willData, navigate]);

  useEffect(() => {
    loadUserProfile();
    
    // When the profile and will data are loaded, fetch the AI suggestion
    if (!isLoadingProfile && !isLoadingWill && willData) {
      fetchAiSuggestion();
    }
  }, [isLoadingProfile, isLoadingWill, willData]);

  const loadUserProfile = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/profiles/me`, {
        headers: {
          'Authorization': `Bearer ${getAccessToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setContactInfo(data.contactInfo || "");
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error("Failed to load contact information");
    } finally {
      setIsLoadingProfile(false);
    }
  };
  
  const handleMediaClick = (index: number) => {
    if (willData?.media && willData.media.length > 0) {
      setInitialMediaIndex(index);
      setIsMediaViewerOpen(true);
    }
  };

  if (isLoadingWill) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        <main className="container mx-auto px-4 pt-24 pb-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
              <p className="text-gray-600">Loading want details...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!willData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        <main className="container mx-auto px-4 pt-24 pb-12">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-xl font-semibold text-red-600 mb-2">Want Not Found</h1>
            <p className="text-gray-600 mb-4">The want you're looking for doesn't exist or has been removed.</p>
            <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800">
              <ArrowLeft className="h-4 w-4 mr-2" /> Return to Home
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const handleSubmit = async (formData: {
    price: string;
    currency: string;
    description: string;
    estimatedCompletionDate: string;
    providerContactInfo: string;
    mediaFiles?: any[];
  }) => {
    if (!formData.description.trim()) {
      toast.error("Please provide a description for your offer");
      return;
    }

    try {
      // Log the form data to see what's being received
      console.log("Form data received:", formData);
      console.log("Media files:", formData.mediaFiles);
      
      await fetch(`${import.meta.env.VITE_API_URL}/profiles/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`
        },
        body: JSON.stringify({
          contactInfo: formData.providerContactInfo
        })
      });

      const offerData = {
        price: formData.price ? parseFloat(formData.price) : undefined,
        currency: formData.currency,
        description: formData.description,
        providerContactInfo: formData.providerContactInfo,
        estimatedCompletionDate: formData.estimatedCompletionDate ? `${formData.estimatedCompletionDate}T14:00:00` : undefined,
      };

      const mediaFiles = formData.mediaFiles?.map(item => item.file).filter(Boolean) || [];
      console.log('MakeOffer - mediaFiles:', mediaFiles);
      console.log('MakeOffer - mediaFiles is array:', Array.isArray(mediaFiles));
      if (Array.isArray(mediaFiles)) {
        mediaFiles.forEach((file, index) => {
          console.log(`MakeOffer - File ${index}:`, file instanceof File, file.name, file.type, file.size);
        });
      }

      let response;
      if (mediaFiles.length > 0) {
        response = await createOfferWithMedia(id || '', offerData, mediaFiles);
        toast.success("Offer with media submitted successfully!");
        navigate(`/will/${id}`, { state: { will: willData } });
      } else {
        const fetchResponse = await createOffer(id || '', offerData);

        if (fetchResponse.status === 409) {
          const errorData = await fetchResponse.json() as ConflictError;
          toast.error(
            <div className="flex flex-col gap-2">
              <p>You already have an offer for this will.</p>
              <button
                onClick={() => navigate(`/my-offers/${errorData.existingOfferId}`)}
                className="text-sm text-blue-500 hover:text-blue-600 underline"
              >
                View your existing offer
              </button>
            </div>,
            {
              duration: 5000,
            }
          );
          return;
        }

        if (!fetchResponse.ok) {
          throw new Error('Failed to submit offer');
        }
        
        toast.success("Offer submitted successfully!");
        navigate(`/will/${id}`, { state: { will: willData } });
      }
    } catch (error) {
      console.error('Error submitting offer:', error);
      toast.error("Failed to submit offer. Please try again.");
    }
  };

  // Extract description safely with proper HTML tag handling
  const getWillDescription = () => {
    if (!willData?.description) return "";
    
    // Simple HTML sanitizing
    const div = document.createElement('div');
    div.innerHTML = willData.description;
    return div.textContent || div.innerText || "";
  };

  // Truncate text for display in the sidebar
  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };
  
  const description = getWillDescription();
  const truncatedDescription = truncateText(description, 180);

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />
      
      <main className="container mx-auto px-4 pt-20 pb-12">
        {/* Mobile view (single column) */}
        <div className="lg:hidden">
          <div className="max-w-lg mx-auto bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Header with back button */}
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <button
                onClick={() => navigate(-1)}
                className="p-1 rounded-sm opacity-70 hover:opacity-100 focus:outline-none"
              >
                <ArrowLeft className="h-5 w-5" />
                <span className="sr-only">Back</span>
              </button>
              <h3 className="text-lg font-medium">Make an Offer: {willData.title}</h3>
              <div className="w-5"></div> {/* Empty div for balanced spacing */}
            </div>
            
            {/* Want Summary Section */}
            <div className="border-b border-gray-200 p-4">
              <div className="flex flex-col space-y-2">
                <div>
                  <span className="text-sm font-medium text-gray-700">Want</span>
                  <p className="text-base font-semibold text-gray-900 mt-1">{willData.title}</p>
                </div>
                
                {willData.reward && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Reward</span>
                    <p className="text-base font-bold text-green-700 mt-1">{willData.reward}</p>
                  </div>
                )}
                
                {willData.fulfillmentTime && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Due by</span>
                    <p className="text-base text-gray-900 mt-1">
                      {new Date(willData.fulfillmentTime).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                )}
                
                {/* Media section removed from mobile view */}
                <div className="pt-2">
                  <Link 
                    to={`/will/${id}`}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    View full want details
                    <svg className="w-3.5 h-3.5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
            
            <div className="p-4">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                  <p className="text-gray-600">Creating AI-suggested offer...</p>
                </div>
              ) : (
                <>
                  {aiSuggestion && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="text-sm font-medium flex items-center">
                            <svg className="w-4 h-4 mr-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                            </svg>
                            AI-Suggested Offer
                          </h4>
                          <p className="text-xs text-gray-600 mt-1">
                            We've created a suggested offer based on the want details.
                          </p>
                        </div>
                        <button
                          onClick={handleRegenerateSuggestion}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center"
                          disabled={loading}
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Regenerate
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-600">
                      {error}
                    </div>
                  )}
                  
                  <OfferForm 
                    willId={id || ''}
                    willMedia={willData.media}
                    onCancel={() => navigate(-1)}
                    onSubmit={handleSubmit}
                    defaultContactInfo={contactInfo}
                    initialData={aiSuggestion}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Desktop view (two columns) */}
        <div className="hidden lg:block max-w-6xl mx-auto">
          <div className="flex mb-4 items-center">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900 text-sm transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Want
            </button>
            <h1 className="text-2xl font-semibold text-center flex-1 pr-10">Make an Offer</h1>
          </div>

          <div className="flex gap-6">
            {/* Left column - Want details */}
            <div className="w-1/3">
              <div className="bg-white rounded-lg shadow-sm overflow-hidden sticky top-24">
                <div className="px-5 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Want Details</h2>
                </div>

                <div className="p-5 space-y-4">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">{willData.title}</h3>
                    <p className="text-sm text-gray-600 mt-2">{truncatedDescription}</p>
                    {description.length > 180 && (
                      <Link 
                        to={`/will/${id}`} 
                        className="text-blue-600 text-xs hover:underline inline-block mt-1"
                      >
                        Read more
                      </Link>
                    )}
                  </div>

                  {willData.reward && (
                    <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                      <span className="text-sm font-medium text-gray-700">Reward</span>
                      <span className="text-base font-bold text-green-700 bg-green-50 px-2 py-1 rounded">
                        {willData.reward}
                      </span>
                    </div>
                  )}

                  {willData.fulfillmentTime && (
                    <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                      <span className="text-sm font-medium text-gray-700">Due by</span>
                      <span className="text-sm text-gray-900">
                        {new Date(willData.fulfillmentTime).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  )}

                  {/* Media section removed from desktop view */}

                  <div className="border-t border-gray-100 pt-3 mt-4">
                    <Link 
                      to={`/will/${id}`}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      View full want details
                      <svg className="w-3.5 h-3.5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column - Offer form */}
            <div className="w-2/3 bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Create Your Offer</h2>
              </div>

              <div className="p-6">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                    <p className="text-gray-600">Creating AI-suggested offer...</p>
                  </div>
                ) : (
                  <>
                    {aiSuggestion && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-sm font-medium flex items-center">
                              <svg className="w-4 h-4 mr-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                              </svg>
                              AI-Suggested Offer
                            </h4>
                            <p className="text-xs text-gray-600 mt-1">
                              We've created a suggested offer based on the want details.
                            </p>
                          </div>
                          <button
                            onClick={handleRegenerateSuggestion}
                            className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center"
                            disabled={loading}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Regenerate
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-600">
                        {error}
                      </div>
                    )}
                    
                    <OfferForm 
                      willId={id || ''}
                      willMedia={willData.media}
                      onCancel={() => navigate(-1)}
                      onSubmit={handleSubmit}
                      defaultContactInfo={contactInfo}
                      initialData={aiSuggestion}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {isMediaViewerOpen && (
        <MediaViewer 
          media={willData.media}
          initialIndex={initialMediaIndex}
          onClose={() => setIsMediaViewerOpen(false)}
        />
      )}
    </div>
  );
};

export default MakeOffer;
